import { getData } from "./data";
import {
  categoryBusinessUrl,
} from "../component/businessSearchDeepLink";
import {
  categoryUrl,
  resolveCategoryMeta,
} from "../component/categoryDirectoryConfig";
import {
  communityUrl,
  resolveCommunityMeta,
} from "../component/communityDirectoryConfig";

const API_BASE_URL = (
  process.env.REACT_APP_GOOURHUB_BUSINESS_API ||
  "https://business-api.goourhub.com"
).replace(/\/+$/, "");

const PAGE_LIMIT = 200;
const MAX_TOTAL_PAGES = 100;

const CORE_PAGES = [
  {
    id: "page:hub",
    type: "Page",
    title: "GoOurHub Hub",
    description:
      "Explore San Antonio communities, local businesses and service categories.",
    keywords: ["hub", "business", "community", "San Antonio", "local"],
    url: "/hub",
  },
  {
    id: "page:hubmart",
    type: "Marketplace",
    title: "HubMart",
    description:
      "Buy, sell, donate and browse local marketplace listings.",
    keywords: ["marketplace", "buy", "sell", "free", "HubMart"],
    url: "/HubMart",
  },
  {
    id: "page:connect",
    type: "Page",
    title: "Connect with GoOurHub",
    description: "Connect with GoOurHub and its local community.",
    keywords: ["connect", "social", "community"],
    url: "/connect",
  },
];

const firstText = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }

  return "";
};

const textList = (value) => {
  if (!Array.isArray(value)) return [];

  return value
    .map((item) => String(item ?? "").trim())
    .filter(Boolean);
};

const parseDetails = (business) => {
  if (
    business?.details &&
    typeof business.details === "object" &&
    !Array.isArray(business.details)
  ) {
    return business.details;
  }

  if (
    business?.details_json &&
    typeof business.details_json === "object" &&
    !Array.isArray(business.details_json)
  ) {
    return business.details_json;
  }

  if (typeof business?.details_json === "string") {
    try {
      const parsed = JSON.parse(business.details_json);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch (error) {
      console.warn("Unable to parse business details_json.", error);
    }
  }

  return {};
};

const locationAddress = (details) => {
  if (!Array.isArray(details?.locations) || details.locations.length === 0) {
    return "";
  }

  const location = details.locations[0];

  if (typeof location === "string") {
    return location.trim();
  }

  if (!location || typeof location !== "object") {
    return "";
  }

  return firstText(
    location.full_address,
    location.fullAddress,
    location.address,
    [
      firstText(
        location.street_address,
        location.streetAddress,
        location.street
      ),
      location.city,
      location.state,
      firstText(location.zip_code, location.zipCode, location.zip),
    ]
      .filter(Boolean)
      .join(", ")
  );
};

function makeCommunityItem(item) {
  const communityName = firstText(
    item?.value,
    item?.people,
    item?.name,
    item?.label
  );

  if (!communityName) return null;

  const meta = resolveCommunityMeta(communityName);
  const destination = firstText(item?.url) || communityUrl(meta.value);

  return {
    id: `community:${meta.value.toLowerCase()}`,
    type: "Community",
    title: `${firstText(item?.people, item?.label, meta.label)} community`,
    description: `Explore ${firstText(
      item?.people,
      item?.label,
      meta.label
    )} businesses, food, services and community guides in San Antonio.`,
    keywords: [
      communityName,
      meta.label,
      "community",
      "culture",
      "restaurants",
      "groceries",
      "businesses",
      "San Antonio",
    ],
    url: destination,
  };
}

function makeCategoryItem(item) {
  const rawCategory = firstText(item?.value, item?.name, item?.label);

  if (!rawCategory) return null;

  const meta = resolveCategoryMeta(rawCategory);
  const destination = firstText(item?.url) || categoryUrl(meta.value);
  const displayName = firstText(item?.name, item?.label, meta.label);

  return {
    id: `category:${meta.value}`,
    type: "Business category",
    title: displayName,
    description: `Find ${displayName.toLowerCase()} and related local businesses in San Antonio.`,
    keywords: [
      displayName,
      meta.value,
      "business",
      "service",
      "local",
      "San Antonio",
    ],
    url: destination,
  };
}

function makeBusinessItem(business) {
  const businessId = firstText(
    business?.id,
    business?.businessId,
    business?.business_id
  );
  const name = firstText(business?.name, business?.business_name);

  if (!businessId || !name) return null;

  const details = parseDetails(business);
  const rawCategory = firstText(
    business?.category,
    business?.business_type,
    details.category
  );
  const categoryMeta = resolveCategoryMeta(rawCategory || "business");
  const address = firstText(
    business?.address,
    business?.street_address,
    details.address,
    details.street_address,
    locationAddress(details)
  );
  const phone = firstText(
    business?.phone_number,
    business?.phoneNumber,
    business?.phone,
    details.phone_number,
    details.phoneNumber,
    details.phone
  );
  const website = firstText(
    business?.website,
    details.website
  );
  const communities = textList(details.communities);
  const languages = textList(details.languages);
  const specialties = textList(details.specialties);
  const categoryTags = textList(details.category_tags);

  const descriptionParts = [categoryMeta.label];

  if (address) {
    descriptionParts.push(address);
  } else if (communities.length > 0) {
    descriptionParts.push(`${communities.slice(0, 2).join(", ")} community`);
  } else {
    descriptionParts.push("San Antonio");
  }

  return {
    id: `business:${businessId}`,
    type: "Business",
    title: name,
    description: descriptionParts.join(" • "),
    keywords: [
      name,
      rawCategory,
      categoryMeta.label,
      address,
      phone,
      website,
      ...categoryTags,
      ...communities,
      ...languages,
      ...specialties,
      "business",
      "San Antonio",
    ].filter(Boolean),
    url: categoryBusinessUrl(business),
    businessId,
  };
}

const uniqueBusinessRecords = (businesses) => {
  const unique = new Map();

  businesses.forEach((business) => {
    const details = parseDetails(business);
    const businessId = firstText(
      business?.id,
      business?.businessId,
      business?.business_id,
      details.canonical_id
    );

    const fallbackKey = [
      firstText(business?.name).toLowerCase(),
      firstText(business?.address).toLowerCase(),
    ].join("|");

    const key = businessId || fallbackKey;

    if (key) {
      unique.set(key, business);
    }
  });

  return Array.from(unique.values());
};

const dynamicCommunitiesFromBusinesses = (businesses) => {
  const names = new Map();

  businesses.forEach((business) => {
    const details = parseDetails(business);

    textList(details.communities).forEach((communityName) => {
      const meta = resolveCommunityMeta(communityName);
      names.set(meta.value.toLowerCase(), {
        value: meta.value,
        label: meta.label,
        url: communityUrl(meta.value),
      });
    });
  });

  return Array.from(names.values());
};

const dynamicCategoriesFromBusinesses = (businesses) => {
  const categories = new Map();

  businesses.forEach((business) => {
    const details = parseDetails(business);
    const values = [
      firstText(business?.category, business?.business_type),
      ...textList(details.category_tags),
    ].filter(Boolean);

    values.forEach((value) => {
      const meta = resolveCategoryMeta(value);
      categories.set(meta.value, {
        value: meta.value,
        name: meta.label,
        url: categoryUrl(meta.value),
      });
    });
  });

  return Array.from(categories.values());
};

export function buildHubSearchIndex(
  language = "english",
  businessRecords = []
) {
  const data = getData(language) || {};
  const businesses = uniqueBusinessRecords(
    Array.isArray(businessRecords) ? businessRecords : []
  );

  const communitySource = [
    ...(data.frequentlyVisitedCommunities || []),
    ...(data.communities || []),
    ...dynamicCommunitiesFromBusinesses(businesses),
  ];

  const categorySource = [
    ...(data.categories || []),
    ...(data.service || []),
    ...dynamicCategoriesFromBusinesses(businesses),
  ];

  const communities = communitySource
    .map(makeCommunityItem)
    .filter(Boolean);

  const categories = categorySource
    .map(makeCategoryItem)
    .filter(Boolean);

  const businessItems = businesses
    .map(makeBusinessItem)
    .filter(Boolean);

  // Use the item ID as the primary unique key. This prevents duplicated
  // communities/categories while preserving exact business URLs.
  const uniqueItems = new Map();

  [
    ...CORE_PAGES,
    ...communities,
    ...categories,
    ...businessItems,
  ].forEach((item) => {
    uniqueItems.set(item.id, item);
  });

  return Array.from(uniqueItems.values());
}

const readBusinessList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.businesses)) return payload.businesses;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data)) return payload.data;

  return [];
};

const fetchBusinessPage = async (page, signal) => {
  const url = new URL(`${API_BASE_URL}/api/businesses`);
  url.searchParams.set("page", String(page));
  url.searchParams.set("limit", String(PAGE_LIMIT));
  url.searchParams.set("sort", "name");

  const response = await fetch(url.toString(), {
    method: "GET",
    headers: {
      Accept: "application/json",
    },
    signal,
  });

  if (!response.ok) {
    throw new Error(
      `Business search request failed with status ${response.status}.`
    );
  }

  return response.json();
};

export async function fetchAllApprovedBusinesses({ signal } = {}) {
  const firstPayload = await fetchBusinessPage(1, signal);
  const firstBusinesses = readBusinessList(firstPayload);
  const totalPages = Math.min(
    Math.max(Number(firstPayload?.pagination?.totalPages) || 1, 1),
    MAX_TOTAL_PAGES
  );

  if (totalPages === 1) {
    return uniqueBusinessRecords(firstBusinesses);
  }

  const remainingPayloads = await Promise.all(
    Array.from(
      { length: totalPages - 1 },
      (_, index) => fetchBusinessPage(index + 2, signal)
    )
  );

  const allBusinesses = [
    ...firstBusinesses,
    ...remainingPayloads.flatMap(readBusinessList),
  ];

  return uniqueBusinessRecords(allBusinesses);
}

export async function loadHubSearchIndex(
  language = "english",
  { signal } = {}
) {
  const businesses = await fetchAllApprovedBusinesses({ signal });
  return buildHubSearchIndex(language, businesses);
}
